<template>
  <div class="slide slide-closing">
    <!-- Decorative Rings -->
    <div class="rings-container">
      <div class="ring ring-outer"></div>
      <div class="ring ring-inner"></div>
    </div>

    <!-- Content -->
    <div class="slide-content">
      <!-- Thank You -->
      <div class="thank-you-section">
        <h1 class="thank-you-title">{{ data.title }}</h1>
        <p class="thank-you-subtitle">{{ data.subtitle }}</p>
      </div>

      <!-- Next Steps -->
      <div class="next-steps-section" v-if="data.nextSteps && data.nextSteps.length">
        <h3 class="section-label">{{ $t('reports.quarterly.closing.next_steps') }}</h3>
        <div class="steps-list">
          <div
            v-for="(step, index) in data.nextSteps"
            :key="index"
            class="step-item"
            :style="{ animationDelay: `${index * 0.1 + 0.3}s` }"
          >
            <svg class="check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="step-text">{{ step }}</span>
          </div>
        </div>
      </div>

      <!-- Contact Box -->
      <div class="contact-box">
        <span class="contact-label">{{ $t('reports.quarterly.closing.contact_label') }}</span>
        <span class="contact-text">{{ $t('reports.quarterly.closing.contact_text') }}</span>
      </div>

      <!-- Footer -->
      <div class="slide-footer">
        <span class="company-name">{{ data.companyName }}</span>
        <span class="separator">|</span>
        <span class="generated-date">{{ $t('reports.quarterly.closing.generated') }} {{ formatDate(data.generatedAt) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ClosingSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-closing {
  @extend .slide-base;
  background: $paper;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

// Decorative Rings
.rings-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba($violet-500, 0.1);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.ring-outer {
  width: 800px;
  height: 800px;
}

.ring-inner {
  width: 600px;
  height: 600px;
  border-style: dashed;
}

.slide-content {
  position: relative;
  z-index: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
}

.thank-you-section {
  margin-bottom: 60px;
}

.thank-you-title {
  @extend .serif-headline;
  font-size: 96px;
  color: $ink;
  margin: 0 0 20px;
}

.thank-you-subtitle {
  font-family: $font-body;
  font-size: 24px;
  color: $ink-muted;
  margin: 0;
}

.next-steps-section {
  margin-bottom: 60px;
  width: 100%;
}

.section-label {
  @extend .mono-label;
  font-size: 14px;
  color: $ink-subtle;
  letter-spacing: 0.1em;
  margin: 0 0 24px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: $shadow-soft;
  animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: $shadow-medium;
  }
}

.check-icon {
  color: $emerald;
  flex-shrink: 0;
}

.step-text {
  font-family: $font-body;
  font-size: 18px;
  color: $ink;
}

.contact-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 48px;
  background: rgba($violet-500, 0.05);
  border: 1px solid rgba($violet-500, 0.2);
  border-radius: 16px;
  margin-bottom: 60px;
}

.contact-label {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-subtle;
}

.contact-text {
  font-family: $font-body;
  font-size: 18px;
  font-weight: 600;
  color: $violet-500;
}

.slide-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: $font-body;
  font-size: 14px;
  color: $ink-subtle;

  .company-name {
    font-weight: 600;
    color: $ink-muted;
  }

  .separator {
    color: rgba($ink, 0.2);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
